/**
 * SUB-4.2 · FR34, FR35. The Account column and the account filter, shared by the
 * merged Projects list and the merged Tasks list.
 *
 * Design B1, chosen 2026-08-13 (`SUB-4.0-phase-3-shared-context.md` §9, D21): a
 * sortable Account column, because sorting and account-filtering both fall out
 * of it.
 *
 * ⚠️ COLOUR IS NOT USED AS A CARRIER HERE, AND THAT IS DELIBERATE. B2's colour
 * rail was approved only as a REDUNDANT cue layered on top of this; colour as
 * the sole carrier of which account a row belongs to fails WCAG 1.4.1. The
 * account is text, and any future rail must sit beside this text rather than
 * replace it.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AccountLabel } from '@/lib/accountAttribution';

/** The `value` a Select uses for "do not narrow". Radix forbids an empty string. */
export const ALL_ACCOUNTS_VALUE = '__all__';

/**
 * One row's account.
 *
 * ⚠️ NEVER RENDERS EMPTY. An unnamed account still says which account it is,
 * because a blank cell in a merged list reads as "the account I am in" and that
 * is the exact mis-attribution FR34 exists to prevent.
 */
export function AccountCell({ label }: { label: AccountLabel }) {
  return (
    <span
      className={label.known ? 'text-foreground' : 'text-muted-foreground italic'}
      title={label.known ? label.name : `Account id ${label.tenantId || 'missing'}`}
    >
      {label.name}
    </span>
  );
}

interface AccountFilterProps {
  /** The accounts the SERVER reported for this scope. Never a client-derived set. */
  options: AccountLabel[];
  /** A tenant id, or null for every account in scope. */
  value: string | null;
  onChange: (tenantId: string | null) => void;
}

/**
 * FR35. Narrow a merged list to one account.
 *
 * The chosen id is handed back to the caller, which applies it to the QUERY.
 * Filtering the fetched rows instead would put a second bound in application
 * code, which is what ISO4 forbids and what a reviewer cannot tell apart from
 * the real one.
 */
export function AccountFilter({ options, value, onChange }: AccountFilterProps) {
  return (
    <Select
      value={value ?? ALL_ACCOUNTS_VALUE}
      onValueChange={(next) =>
        onChange(next === ALL_ACCOUNTS_VALUE ? null : next)
      }
    >
      <SelectTrigger className="w-[220px]" aria-label="Filter by account">
        <SelectValue placeholder="All accounts" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_ACCOUNTS_VALUE}>All accounts</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.tenantId} value={option.tenantId}>
            {option.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
